import React, { useEffect, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import Cookies from "js-cookie";

import { useTranslation } from "react-i18next";
import i18n, { changeLanguage } from "i18next";

import { assetService } from "../services/assetService";
import { partnerService, Partner } from "../services/partnerService";
import { systemService } from "../services/systemService";
import { partnerMTLService, MTL } from "../services/partnerMtlService";
import { partnerInterpreterService } from "../services/partnerInterpreterService";
import { partnerAliveService } from "../services/partnerAliveService";
import { partnerAzayService } from "../services/partnerAzayService";
import { partnerLineService } from "../services/partnerLineService";
import { partnerSatService } from "../services/partnerSatService";
import { partnerAgewellService } from "../services/partnerAgewellService";
import { partnerThaiLifeService } from "../services/partnerThaiLifeService";
import { partnerWellSuperAppService, WellSuperApp } from "../services/partnerWellSuperAppService";

import { toMainApp } from "../helpers/toMainApp";
import { checkAndReloadWithExternalBrowserParam } from "../helpers/checkAndReloadWithExternalBrowserParam";
import { normalizeAgreementLinks } from "../helpers/normalizeAgreementLinks";
import { getAppBasePath } from "../helpers/getAppBasePath";

import ThaiLifeBackButton from "../components/buttons/ThaiLifeBackButton";
import WellSuperAppHomeButton from "../components/buttons/WellSuperAppHomeButton";
import LanguageToggle from "../components/buttons/LanguageToggle";

import ErrorModal from "../components/modals/ErrorModal";
import LoadingModal from "../components/modals/LoadingModal";
import Spinner from "../components/Spinner";
// import UnsupportedBrowserModal from "../components/modals/UnsupportedBrowserModal";

// import { PreparingYourSession } from "./callReadinessExp/PreparingYourSession";

import { useAgreementLogic } from "../hooks/useAgreementLogic";
// import { useEnvironmentChecks } from "../hooks/useEnvironmentChecks";
// import { useUnsupportedBrowserModal } from "../hooks/useUnsupportedBrowserModal";

import { getPageConfigField, getPartnerConfigField } from "../utils/getConfigs";

// Partners with extra padding for agreement content
const PARTNERS_WITH_EXTRA_PADDING = ["sth", "ssh", "sch", "sct"];

export default function AgreementPage() {
  const pageKey = "agreement";
  const isShowBackButton = getPageConfigField(pageKey, "isShowBackButton");
  const isShowThaiLifeBackButton = getPageConfigField(
    pageKey,
    "isShowThaiLifeBackButton",
  );
  const isShowWellSuperAppHomeButton = getPageConfigField(
    pageKey,
    "isShowWellSuperAppHomeButton",
  );
  const redirectUrl = getPartnerConfigField("redirectUrl");

  const [isWellPartner, setIsWellPartner] = useState(false);
  const [isThaiLifePartner, setIsThaiLifePartner] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const redirectUrlPath =
    typeof redirectUrl === "string"
      ? redirectUrl
      : typeof toMainApp === "string"
        ? toMainApp
        : "";

  const appBasePath = getAppBasePath();
  const privacyLinkPath = `${appBasePath}/privacy`;

  const wellSuperAppHomeIcon = assetService.getWellSuperAppHome();

  const [partner, setPartner] = useState<Partner | undefined>();
  const [logo, setLogo] = useState<string>();

  const [isShowCookiesBanner, setIsShowCookiesBanner] = useState(false);
  const system = systemService.getSystem(window.location);

  // useAgreementLogic handles the *agree* action (isLoading = processing agree)
  const {
    agree,
    isLoading, // *agree* action loading
  } = useAgreementLogic(partner);

  // local fetch/loading for agreement content
  const [loading, setLoading] = useState<boolean>(true); // the agreement fetch loading
  const [agreementHeader, setAgreementHeader] = useState<string>("");
  const [agreementText, setAgreementText] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef<number>(0);
  const [canAgree, setCanAgree] = useState(false);

  // const { testState } = useEnvironmentChecks();
  const testState = "finished"; // for skipping environment checks
  // const {
  //   handleLeaveMessageConfirm,
  //   redirectToChrome,
  //   isLoading: isUnsupportedBrowserLoading,
  // } = useUnsupportedBrowserModal();

  const [isShowErrorModal, setIsShowErrorModal] = useState(false);

  // Partner init
  useEffect(() => {
    // onMountedPartnerUx
    const initialize = async () => {
      try {
        const p = await partnerService.initPartner(window.location);
        // For WellSuperApp/Well_AIDOCTOR, skip the early setPartner() call.
        // onIndexMounted() is async and must complete first (to map hospitalId → hospitalName).
        // If we call setPartner(p) here, fetchAgreement fires immediately with hospitalName=undefined,
        // and won't re-fire after onIndexMounted() because partner.name doesn't change.
        if (!partnerWellSuperAppService.isActive()) {
          setPartner(p);
        }

        // Partner: SAT
        if (partnerSatService.isActive()) {
          partnerSatService.onIndexMounted(window.location);
          const partner = partnerService.getPartner() as Partner;
          setPartner(partner);
          await navigate("/register");
          return;
        }

        // Partner: Agewell
        else if (partnerAgewellService.isActive()) {
          partnerAgewellService.onIndexMounted(window.location);
          const partner = partnerService.getPartner() as Partner;
          setPartner(partner);
          await navigate("/register");
          return true;
        }

        // Partner: Alive
        else if (partnerAliveService.isActive()) {
          partnerAliveService.onIndexMounted(window.location);
          const partner = partnerService.getPartner() as Partner;
          setPartner(partner);
        }

        // Partner: Azay or AllianzAyudhya
        else if (partnerAzayService.isActive()) {
          partnerAzayService.onIndexMounted();
          const partner = partnerService.getPartner() as Partner;
          setPartner(partner);
          if (!system.acceptCookiesConsent) {
            setIsShowCookiesBanner(true);
          }
        }

        // Partner: Interpreter
        else if (partnerInterpreterService.isActive()) {
          partnerInterpreterService.onIndexMounted();
          setPartner(partnerService.getPartner() as Partner);
        }

        // Partner: MTL
        else if (partnerMTLService.isActive()) {
          partnerMTLService.onIndexMounted(window.location);
          const partner = partnerService.getPartner() as Partner;

          // Detect language.
          if (system.language !== "en" && (partner as MTL).type === "MTL-EN") {
            changeLanguage("en");
          } else if (
            system.language !== "th" &&
            (partner as MTL).type !== "MTL-EN"
          ) {
            changeLanguage("th");
          }
        }

        // Partner: Line
        else if (partnerLineService.isActive()) {
          partnerLineService.onIndexMounted(window.location);
          setPartner(partnerService.getPartner() as Partner);
        }

        // Partner: WellSuperApp and Well_AIDOCTOR
        else if (partnerWellSuperAppService.isActive()) {
          await partnerWellSuperAppService.onIndexMounted(window.location);
          const partner = partnerService.getPartner() as Partner;
          setPartner(partner);
        }

        return false;
      } catch (err) {
        console.error("init partner error", err);
        setIsShowErrorModal(true);
      }
    };

    initialize();
  }, [navigate]);

  // onMountedPartnerUx
  useEffect(() => {
    if (checkAndReloadWithExternalBrowserParam()) return;

    if (partner) {
      setLogo(assetService.getLogo(partner.name));
    }

    // Partner: WellSuperApp.
    if (partnerWellSuperAppService.isActive()) {
      setIsWellPartner(true);
    }

    // Partner: ThaiLife.
    if (partnerThaiLifeService.isActive()) {
      setIsThaiLifePartner(true);
    }
  }, [partner]);

  // Fetch agreement text (local loading)
  useEffect(() => {
    let cancelled = false;
    const fetchAgreement = async () => {
      // Increment request ID to track this request
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;

      setLoading(true);
      setError(false);
      setCanAgree(false);
      // Reset scroll position when loading new agreement
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      try {
        // Get hospitalName if this is a WellSuperApp/Well_AIDOCTOR partner
        const wellPartner = partner as WellSuperApp | undefined;
        const hospitalName = wellPartner?.hospitalName;

        const agreement =
          (await systemService.getAgreement(
            "UsageTerms",
            partner?.name ?? "",
            hospitalName,
          )) || null;

        // Only update state if this is still the latest request and not cancelled
        if (!cancelled && currentRequestId === requestIdRef.current) {
          if (agreement) {
            const currentLang = i18n.language === "th" ? "Th" : "En";
            setAgreementHeader(
              agreement[`AgreementHeader${currentLang}`] || "",
            );
            const currentAgreementHtml =
              agreement[`AgreementHtml${currentLang}`] || "";
            setAgreementText(
              normalizeAgreementLinks(currentAgreementHtml, privacyLinkPath),
            );
          } else {
            setError(true);
          }
        }
        // console.log("Agreement response:", agreement); // for debugging
        // console.log("Language:", i18n.language); // for debugging
      } catch (err) {
        console.error("Error fetching agreement:", err);
        if (!cancelled && currentRequestId === requestIdRef.current)
          setError(true);
      } finally {
        if (!cancelled && currentRequestId === requestIdRef.current)
          setLoading(false);
      }
    };

    if (partner?.name) {
      fetchAgreement();
    }

    return () => {
      cancelled = true;
    };
  }, [i18n.language, partner?.name]);

  // scroll tracking for enabling "agree"
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setCanAgree(scrollTop + clientHeight >= scrollHeight - 10);
      }
    };

    const cur = contentRef.current;
    cur?.addEventListener("scroll", handleScroll);
    return () => cur?.removeEventListener("scroll", handleScroll);
  }, []);

  const goToWellSuperAppHome = () => {
    // console.log("Home button clicked"); // for debugging
    try {
      if (
        typeof window !== "undefined" &&
        "WellWebViewAPI" in window &&
        typeof (window as any).WellWebViewAPI.home === "function"
      ) {
        (window as any).WellWebViewAPI.home();
      } else {
        throw new Error("WellWebViewAPI.home is not available");
      }
    } catch (error) {
      console.error("Error calling WellWebViewAPI.home():", error);
      // Fallback to redirecting to Samitivej Hospitals website
      window.location.href = "https://www.samitivejhospitals.com/";
    }
  };

  const handleCloseErrorModal = async () => {
    window.location.reload();
    localStorage.clear();
    sessionStorage.clear();
    Cookies.remove("modalShown");
    setIsShowErrorModal(false);
  };

  const acceptCookiesConsent = () => {
    setIsShowCookiesBanner(false);
    system.acceptCookiesConsent = true;
    systemService.setSystem(system);
  };

  // IMPORTANT: decide global overlay vs inline spinner
  // Show global overlay only when performing the Agree action (isLoading).
  // Show inline "loading" inside agreement box while fetching agreement (loading).

  return (
    <div className="flex flex-col items-center p-4 md:p-10 gap-y-4 justify-center min-h-screen w-full bg-gray-50">
      {/* {testState === "runningEnvironmentChecks" && <PreparingYourSession />} */}

      {/* {testState === "unsupported" && (
        <UnsupportedBrowserModal
          isOpen={testState === "unsupported"}
          onClose={handleLeaveMessageConfirm}
          onConfirm={redirectToChrome}
          isLoading={isUnsupportedBrowserLoading}
        />
      )} */}

      {testState === "finished" && (
        <>
          {isShowErrorModal ? (
            <ErrorModal
              isVisible={isShowErrorModal}
              onClose={() => setIsShowErrorModal(false)}
              message={"Session expired or invalid partner. Please try again."}
              title={"Sorry, something went wrong"}
              isCancelable={false}
              onConfirm={handleCloseErrorModal}
              confirmButtonText={t("modals.agreement.confirmButton")}
            />
          ) : isLoading ? (
            // isLoading -> show global modal while agree() is in progress
            <LoadingModal message={t("modals.agreement.processing")} />
          ) : (
            <div className="flex flex-col items-center gap-y-4 w-full bg-gray-50">
              <div className="w-full max-w-2xl border p-4 md:p-6 rounded-3xl shadow-md bg-white relative">
                <div className="flex flex-row justify-between items-center w-full">
                  {(isShowThaiLifeBackButton || isThaiLifePartner) && (
                    <ThaiLifeBackButton redirectUrl={redirectUrlPath} />
                  )}
                  {(isShowWellSuperAppHomeButton || isWellPartner) && (
                    <WellSuperAppHomeButton
                      goToWellSuperAppHome={goToWellSuperAppHome}
                      icon={wellSuperAppHomeIcon}
                    />
                  )}
                  <div className="flex justify-end w-full">
                    <LanguageToggle />
                  </div>
                </div>

                <div className="flex flex-col gap-y-5 justify-center items-center mb-4">
                  {logo && (
                    <img src={logo} width={172} height={40} alt="svvh-logo" />
                  )}

                  <div
                    className="text-center"
                    dangerouslySetInnerHTML={{ __html: agreementHeader || "" }}
                  />
                </div>

                <div className="relative">
                  <div
                    ref={contentRef}
                    className={`h-[350px] overflow-y-auto border mb-4 bg-gray-100 rounded agreement-content py-2 ${
                      partner?.name &&
                      PARTNERS_WITH_EXTRA_PADDING.includes(
                        partner.name.toLowerCase(),
                      )
                        ? "px-2"
                        : "px-2"
                    }`}
                  >
                    {loading ? (
                      // local/fetching loading (inline)
                      <div className="flex flex-col items-center justify-center gap-y-2 py-6">
                        <Spinner size="medium" color="gray" />
                        <p className="text-center text-sm text-gray-600">
                          {t("generals.loading")}
                        </p>
                      </div>
                    ) : error ? (
                      <ErrorModal
                        isVisible={error}
                        onClose={() => setError(false)}
                        message={t("modals.agreement.errors.message")}
                        title={t("modals.agreement.errors.title")}
                        isCancelable={false}
                        onConfirm={() => window.location.reload()}
                        confirmButtonText={t("modals.agreement.confirmButton")}
                      />
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: agreementText || "",
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-center gap-x-4 mt-4">
                  {isShowBackButton && (
                    <button
                      onClick={() => (window.location.href = redirectUrlPath)}
                      className="px-4 py-2 rounded-3xl text-svvh-green border border-svvh-green hover:bg-svvh-light-green w-[120px]"
                    >
                      {t("modals.agreement.buttons.back")}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      // set modalShown cookie before calling agree()
                      Cookies.set("modalShown", "true", { expires: 1 });
                      agree();
                    }}
                    disabled={
                      !canAgree || loading || isLoading || Boolean(error)
                    }
                    className={`px-[8px] py-[10px] rounded-3xl text-white w-[120px] ${
                      canAgree
                        ? "gold-submit-btn"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {t("modals.agreement.buttons.next")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {isShowCookiesBanner && (
        <div
          id="cookiesBanner"
          className="fixed bottom-0 left-0 w-full bg-gray-800 text-white p-4 flex flex-col md:flex-row items-center justify-between gap-4 z-50"
        >
          <p className="text-sm md:text-base max-w-6xl">
            {t(
              "We use cookies to help our website work properly and to provide you with a personalized user experience. If you continue without changing your settings, we'll assume that you accept all cookies on our website. However, you can change your cookie settings at any time.",
            )}{" "}
            <Link
              to="/privacy"
              target="_blank"
              className="underline hover:text-gray-300"
            >
              {t("Find out more")}
            </Link>
          </p>
          <button
            onClick={() => {
              acceptCookiesConsent();
            }}
            className="mt-2 md:mt-0 px-4 py-2 bg-green-700 hover:bg-green-600 rounded text-white"
          >
            {t("Accept")}
          </button>
        </div>
      )}
    </div>
  );
}
